// src/theme/websiteTheme.ts
import { createTheme, type Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

// Shared "shine sweep" hover effect for contained buttons — a diagonal light
// band that travels across on hover, layered under the gradient background.
const shineSweep = {
  position: 'relative' as const,
  overflow: 'hidden' as const,
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    top: 0,
    left: '-75%',
    width: '50%',
    height: '100%',
    background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent)',
    transform: 'skewX(-20deg)',
    transition: 'left .55s ease',
  },
  '&:hover::after': { left: '125%' },
};

const buttonOverrides = {
  defaultProps: { disableElevation: true },
  styleOverrides: {
    root: {
      textTransform: 'none' as const,
      borderRadius: 14,
      fontWeight: 700,
      transition: 'transform .18s ease, box-shadow .18s ease',
      '&:active': { transform: 'scale(0.97)' },
    },

    // Disabled gradient buttons keep white text (MUI's default grey is unreadable on the gradient)
    contained: { '&.Mui-disabled': { color: '#fff', opacity: 1, filter: 'saturate(0.6) brightness(1.05)' } },

    // Submit (primary)
    containedPrimary: {
      ...shineSweep,
      // --qs-a / --qs-b are set per section by AppShell (payments green, notes purple, videos red, ...).
      background: 'linear-gradient(90deg, var(--qs-a, #6226A7), var(--qs-b, #F6A938))',
      backgroundSize: '200% auto',
      color: '#fff',
      boxShadow: '0 8px 20px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.35)',
      '&:hover': {
        backgroundPosition: 'right center',
        boxShadow: '0 12px 26px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.35)',
        transform: 'translateY(-1px)',
      },
    },

    // Choose Files (info) — gradient blue
    containedInfo: {
      ...shineSweep,
      background: 'linear-gradient(90deg, #22D3EE 0%, #6226A7 50%, #F6A938 100%)',
      color: '#fff',
      boxShadow: '0 6px 18px rgba(34,211,238,0.3)',
      '&:hover': {
        background: 'linear-gradient(90deg, #0fb8d6 0%, #4e1f84 50%, #db8b21 100%)',
        transform: 'translateY(-1px)',
      },
    },

    // Cancel (error)
    containedError: {
      ...shineSweep,
      background: 'linear-gradient(90deg, #FF3D71 0%, #CF1E52 60%, #6226A7 100%)',
      color: '#fff',
      boxShadow: '0 6px 18px rgba(207,30,82,0.3)',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 10px 24px rgba(255,61,113,0.4)' },
    },

    // Back (outlined + secondary) — gradient border
    outlinedSecondary: {
      borderWidth: 2,
      borderStyle: 'solid' as const,
      borderColor: 'transparent',
      background:
        'linear-gradient(#fff, #fff) padding-box,' +
        'linear-gradient(90deg, #6226A7, #EC4899, #F6A938) border-box',
      color: '#6226A7',
      transition: 'transform .18s ease',
      '&:hover': {
        background:
          'linear-gradient(#FDF7EE, #FDF7EE) padding-box,' +
          'linear-gradient(90deg, #4e1f84, #c23c7f, #db8b21) border-box',
        borderColor: 'transparent',
        transform: 'translateY(-1px)',
      },
    },

    // Show/Hide (secondary)
    containedSecondary: {
      ...shineSweep,
      background: 'linear-gradient(90deg, #F6A938, #EC4899 55%, #6226A7)',
      color: '#fff',
      boxShadow: '0 6px 18px rgba(246,169,56,0.3)',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 10px 24px rgba(236,72,153,0.4)' },
    },
  },
};

export const getWebsiteTheme = (mode: ThemeMode): Theme =>
  createTheme({
    palette:
      mode === 'light'
        ? {
            mode: 'light',
            primary: { main: '#6226A7' },
            secondary: { main: '#F6A938' },
            info: { main: '#0277bd' },
            error: { main: '#CF1E52' },
            background: { default: '#F6F5FB', paper: '#FFFFFF' },
          }
        : {
            mode: 'dark',
            primary: { main: '#9B6FD1' },
            secondary: { main: '#F6A938' },
            info: { main: '#4FC3F7' },
            error: { main: '#EF5382' },
            background: { default: '#0F1115', paper: '#171923' },
            text: { primary: '#EDEDF0', secondary: '#A2A6B0' },
          },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: ['Inter', 'system-ui', 'Segoe UI', 'Roboto', 'Helvetica', 'Arial'].join(','),
      h4: { fontWeight: 700, letterSpacing: -0.5 },
      h5: { fontWeight: 700 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@keyframes qsFadeInUp': {
            from: { opacity: 0, transform: 'translateY(14px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes qsGradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          '@keyframes qsPulseGlow': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(98,38,167,0.45)' },
            '50%': { boxShadow: '0 0 0 8px rgba(98,38,167,0)' },
          },
          '@keyframes qsPing': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '100%': { transform: 'scale(2.4)', opacity: 0 },
          },
          // 'backwards' (not 'both'): a lingering transform would become the containing block of every position:fixed
          // descendant (video player, save bar, loaders), pinning them to the page instead of the viewport.
          '.qs-animate-in': { animation: 'qsFadeInUp .5s ease backwards' },
          // Soft aurora washes behind every page, matching the dashboard hero.
          body: {
            backgroundAttachment: 'fixed',
            backgroundImage:
              mode === 'light'
                ? 'radial-gradient(900px 520px at 8% -8%, rgba(98,38,167,0.12), transparent 60%), radial-gradient(800px 520px at 100% 0%, rgba(246,169,56,0.14), transparent 55%), radial-gradient(700px 500px at 50% 110%, rgba(236,72,153,0.08), transparent 60%)'
                : 'radial-gradient(900px 520px at 8% -8%, rgba(155,111,209,0.20), transparent 60%), radial-gradient(800px 520px at 100% 0%, rgba(246,169,56,0.10), transparent 55%)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            backgroundImage: 'none',
            border: `1px solid ${mode === 'light' ? 'rgba(98,38,167,0.08)' : 'rgba(255,255,255,0.07)'}`,
            boxShadow:
              mode === 'light'
                ? '0 8px 26px rgba(98,38,167,0.07)'
                : '0 8px 26px rgba(0,0,0,0.45)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 22,
            backgroundImage: 'none',
            border: `1px solid ${mode === 'light' ? 'rgba(98,38,167,0.08)' : 'rgba(255,255,255,0.07)'}`,
            transition: 'transform .22s ease, box-shadow .22s ease',
            boxShadow:
              mode === 'light'
                ? '0 10px 30px rgba(98,38,167,0.08)'
                : '0 10px 30px rgba(0, 0, 0, 0.5)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow:
                mode === 'light'
                  ? '0 20px 40px rgba(98,38,167,0.18)'
                  : '0 20px 40px rgba(0,0,0,0.6)',
            },
          },
        },
      },
      MuiButton: buttonOverrides,
      MuiIconButton: { styleOverrides: { root: { borderRadius: 14 } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 600, transition: 'transform .15s ease' },
          clickable: { '&:hover': { transform: 'translateY(-1px) scale(1.03)' } },
        },
      },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 14 } } },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 26,
            boxShadow: '0 30px 80px rgba(46,15,94,0.35)',
          },
        },
      },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 16, marginTop: 6 } } },
      MuiPopover: { styleOverrides: { paper: { borderRadius: 16 } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 10, fontWeight: 600 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 16 } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999 } } },
      MuiToggleButtonGroup: { styleOverrides: { root: { borderRadius: 14 } } },
      MuiToggleButton: { styleOverrides: { root: { borderRadius: 12, textTransform: 'none', fontWeight: 700 } } },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 22, overflow: 'auto' } } },
      MuiTableCell: {
        styleOverrides: {
          head: {
            background: mode === 'light'
              ? 'linear-gradient(180deg, #F5EEFF, #EFE6FB)'
              : 'linear-gradient(180deg, rgba(155,111,209,.18), rgba(155,111,209,.08))',
          },
        },
      },
      MuiAvatar: { styleOverrides: { root: { borderRadius: '50%' } } },
    },
  });

/** @deprecated use getWebsiteTheme('light') instead */
export default getWebsiteTheme('light');
